import { z } from 'zod';
import { DataOriginSchema } from './common';

/**
 * Canonical verification model (ADR-004 A): four INDEPENDENT dimensions.
 * There is intentionally no single `verified` flag anywhere in the legal contracts.
 */
export const VerificationDimensionSchema = z.enum([
  'authenticity',
  'text_integrity',
  'legal_status',
  'legal_analysis',
]);
export type VerificationDimension = z.infer<typeof VerificationDimensionSchema>;

const ReviewMeta = {
  reviewedAt: z.string().optional(),
  reviewerRole: z.enum(['legal_editor', 'legal_reviewer']).optional(),
  note: z.string().optional(),
};

export const LegalStatusValueSchema = z.enum([
  'in_force',
  'partly_in_force',
  'not_commenced',
  'repealed',
  'historical',
]);
export type LegalStatusValue = z.infer<typeof LegalStatusValueSchema>;

export const SourceVerificationSchema = z.object({
  authenticity: z.object({ verdict: z.enum(['pending', 'reviewed', 'rejected']), ...ReviewMeta }),
  text_integrity: z.object({ verdict: z.enum(['pending', 'reviewed', 'rejected']), ...ReviewMeta }),
  legal_status: z.object({
    verdict: z.enum(['pending', 'reviewed', 'unknown']),
    /** The date the status assessment is valid as of. Required to display a reviewed status. */
    asOf: z.string().optional(),
    status: LegalStatusValueSchema.optional(),
    ...ReviewMeta,
  }),
  legal_analysis: z.object({
    verdict: z.enum(['pending', 'reviewed', 'disputed', 'not_applicable']),
    ...ReviewMeta,
  }),
});
export type SourceVerification = z.infer<typeof SourceVerificationSchema>;

export const SourceKindSchema = z.enum(['statute', 'judgment', 'rule', 'regulation', 'other']);
export type SourceKind = z.infer<typeof SourceKindSchema>;

export const LegalPassageSchema = z.object({
  id: z.string(),
  versionId: z.string(),
  label: z.string(),
  position: z.number().int().nonnegative(),
  text: z.string(),
});
export type LegalPassage = z.infer<typeof LegalPassageSchema>;

export const LegalVersionSchema = z.object({
  id: z.string(),
  sourceId: z.string(),
  versionLabel: z.string(),
  contentHash: z.string(),
  publishedAt: z.string().optional(),
  effectiveFrom: z.string().optional(),
  effectiveTo: z.string().optional(),
  verification: SourceVerificationSchema,
  passages: z.array(LegalPassageSchema),
});
export type LegalVersion = z.infer<typeof LegalVersionSchema>;

export const LegalSourceSchema = z.object({
  id: z.string(),
  dataOrigin: DataOriginSchema,
  kind: SourceKindSchema,
  title: z.string(),
  jurisdiction: z.string(),
  court: z.string().optional(),
  citationAliases: z.array(z.string()),
  provenance: z.object({ publisher: z.string(), acquisition: z.string(), rightsNote: z.string() }),
  versions: z.array(LegalVersionSchema),
});
export type LegalSource = z.infer<typeof LegalSourceSchema>;

/** A citation always resolves to source + version + passage IDs; it carries no verified flag. */
export const LegalCitationSchema = z.object({
  id: z.string(),
  sourceId: z.string(),
  versionId: z.string(),
  passageIds: z.array(z.string()).min(1),
  label: z.string(),
  asOfDate: z.string().optional(),
  dataOrigin: DataOriginSchema,
});
export type LegalCitation = z.infer<typeof LegalCitationSchema>;
